import type { Page } from '../../types';

const yourDetails: Page[] = [
  {
    pageType: 'question',
    title: 'Your Details',
    name: 'your-details',
    hideSubTitle: true,
    description: 'Please complete your personal information.',
    questions: [
      {
        questionType: 'textInput',
        title: 'Full Name',
        name: 'full-name',
        gdpr: 'personal',
        placeholder: 'Anne Alytics',
        rules: {
          required: true,
        },
      },
      {
        questionType: 'textInput',
        title: 'Email Address',
        name: 'contact-email',
        gdpr: 'personal',
        placeholder: 'your.name@cabinetoffice.gov.uk',
        restrictDomains: true,
        rules: {
          required: true,
          email: true,
        },
      },
      {
        questionType: 'textInput',
        title: 'SOP Employee Number',
        name: 'employee-number',
        hint: 'Numbers only, between 4 and 8 digits',
        placeholder: '12345678',
        rules: {
          required: true,
          number: true,
          pattern: '^\\d{4,8}$',
        },
        errorMessages: {
          number: 'Enter your SOP employee number using numbers only',
          pattern: 'Enter an SOP employee number between 4 and 8 digits',
        },
      },
      {
        questionType: 'autoComplete',
        title: 'Business Area',
        hint: 'If you cannot find your business area, please enter: Other / Not listed',
        name: 'business-area',
        items: [
          'Borders',
          'CFO Directorate',
          'Central Digital and Data Office',
          'Civil Service Commission',
          'Civil Service Strategy Unit',
          'CO Career Transition Service',
          'CO Central Budgets',
          'CO Digital',
          'CO HMT Commercial',
          'CO People and Places',
          'CO Strategy Directorate (incl. Ministerial Private Offices and COO)',
          'Corporate Services Transformation',
          'Covid Inquiry',
          'Crown Commercial Service',
          'Digital ID',
          'Domestic and Economic Secretariat',
          'Efficiency and Reform Group (Public Sector Reform PSREU)',
          'Equality and Human Rights Commission',
          'European & Global Issues Directorate',
          'Government Commercial and Grants Function',
          'Government Commercial Organisation (non-CO)',
          'Government Communication Service',
          'Government Digital Service',
          'Government in Parliament',
          'Government People Group',
          'Government People Group - Faststream',
          'Government Property Agency',
          'Government Security Group',
          'Government Security Group - UKSV',
          'Grenfell Inquiry',
          'Infected Blood Compensation Authority',
          'Infected Blood Inquiry',
          'Infrastructure and Projects Authority',
          'Intelligence and Security Committee',
          'Joint Intelligence Organisation',
          'National Security Secretariat',
          'Office for Equality and Opportunity',
          "Office for Veterans' Affairs",
          'Office of Government Property',
          'Office of the Registrar of Consultant Lobbyists',
          'Other / Not listed',
          "Prime Minister's Office",
          'Propriety & Constitution Group',
          'Public Inquiry Response Unit',
          'Public Sector Fraud Authority',
        ],
        options: [
          {
            option: 'Other / Not listed',
            questions: [
              {
                questionType: 'textInput',
                title: 'Please specify',
                name: 'business-area-other',
                rules: {
                  required: true,
                },
              },
            ],
          },
        ],
        rules: {
          required: true,
        },
      },
      {
        questionType: 'textInput',
        title: 'Team Name',
        name: 'team-name',
        rules: {
          required: true,
        },
      },
      {
        questionType: 'select',
        title: 'Office Location',
        name: 'office-location-region',
        hint: 'If you are a contractual homeworker, select your designated office for pay purposes',
        hintOption: 'Select an option',
        options: [
          {
            option: 'London and Southern England',
            questions: [
              {
                questionType: 'select',
                title: 'Please select',
                name: 'office-location',
                hintOption: 'Please select',
                options: [
                  'Basingstoke - Priestley House',
                  'Bristol - 3 Glass Wharf',
                  'London - 1 Horse Guards Road',
                  'London - 10 South Colonnade',
                  'London - 10 Victoria Street',
                  'London - 100 Parliament Street',
                  'London - 35 Great Smith Street',
                  'London - 70 Whitehall',
                  'London - Admiralty House',
                  'Milton Keynes - Hanslope Park',
                  'Norwich - Rosebery Court',
                  {
                    option: 'Other',
                    questions: [
                      {
                        questionType: 'textInput',
                        title: 'Please select',
                        name: 'office-location-other',
                        rules: { required: true },
                      },
                    ],
                  },
                ],
                rules: {
                  required: true,
                },
              },
            ],
          },
          {
            option: 'Northern England',
            questions: [
              {
                questionType: 'select',
                title: 'Please select',
                name: 'office-location',
                hintOption: 'Please select',
                options: [
                  'Leeds 7-8 Wellington Place',
                  'Liverpool - The Capital Building',
                  "Manchester - 2 St Peter's Square",
                  'Newcastle - Benton Park View',
                  'Sheffield - Kings Court',
                  'York - Foss House',
                  'York - Imphal Barracks',
                  {
                    option: 'Other',
                    questions: [
                      {
                        questionType: 'textInput',
                        title: 'Please select',
                        name: 'office-location-other',
                        rules: { required: true },
                      },
                    ],
                  },
                ],
                rules: {
                  required: true,
                },
              },
            ],
          },
          {
            option: 'Northern Ireland, Scotland and Wales',
            questions: [
              {
                questionType: 'select',
                title: 'Please select',
                name: 'office-location',
                hintOption: 'Please select',
                options: [
                  'Belfast - Erskine House',
                  'Cardiff - Ty William Morgan',
                  'Edinburgh - Queen Elizabeth House',
                  'Glasgow - 1 Atlantic Square',
                  {
                    option: 'Other',
                    questions: [
                      {
                        questionType: 'textInput',
                        title: 'Please select',
                        name: 'office-location-other',
                        rules: { required: true },
                      },
                    ],
                  },
                ],
                rules: {
                  required: true,
                },
              },
            ],
          },
          {
            option: 'The Midlands',
            questions: [
              {
                questionType: 'select',
                title: 'Please select',
                name: 'office-location',
                hintOption: 'Please select',
                options: [
                  'Birmingham - 23 Stephenson Street',
                  {
                    option: 'Other',
                    questions: [
                      {
                        questionType: 'textInput',
                        title: 'Please select',
                        name: 'office-location-other',
                        rules: { required: true },
                      },
                    ],
                  },
                ],
                rules: {
                  required: true,
                },
              },
            ],
          },
        ],
        rules: {
          required: true,
        },
      },
    ],
  },
];

export default yourDetails;
